import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { EngineMaintenanceDetailDto } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { round } from 'src/util/math';

interface EngineDetailControls {
  starts: AbstractControl;
  time: AbstractControl;
  cycleControls?: { name: string; control: AbstractControl }[];
}

@Component({
  selector: 'fl-engine-maintenance',
  templateUrl: './engine-maintenance.component.html',
  styleUrls: ['./engine-maintenance.component.scss'],
})
export class EngineMaintenanceComponent implements OnInit {
  @Input()
  public engine: FormGroup;
  @Input()
  public engineNumber: number;
  @Input()
  public maintenanceParameters: string[];
  @Input()
  public previousTotals: EngineMaintenanceDetailDto;

  get shortName() {
    return `E${this.engineNumber}`;
  }

  get time() {
    return this.engine.get('time');
  }

  get starts() {
    return this.engine.get('starts');
  }

  get name() {
    return `Engine ${this.engineNumber}`;
  }

  get current(): EngineDetailControls {
    return {
      // tslint:disable-next-line: no-non-null-assertion
      starts: this.starts!,
      // tslint:disable-next-line: no-non-null-assertion
      time: this.time!,
      cycleControls: this.cycleControls,
    };
  }

  get previous(): EngineDetailControls | undefined {
    // previous totals are static and readonly
    if (this.previousTotals != null) {
      return {
        starts: new FormControl(this.previousTotals.starts),
        time: new FormControl(this.previousTotals.time),
        cycleControls: this.maintenanceParameters.map((mp) => {
          return {
            name: mp,
            control:
              this.previousTotals.cycles[mp] != null
                ? new FormControl(this.previousTotals.cycles[mp])
                : new FormControl(0),
          };
        }),
      };
    }
  }

  get totals(): EngineDetailControls | undefined {
    const previous =
      this.previous != null
        ? // tslint:disable-next-line: no-non-null-assertion
          this.previous!
        : {
            time: new FormControl(0),
            starts: new FormControl(0),
            cycleControls: [],
          };

    const cycleControls = this.maintenanceParameters.map((mp) => {
      const prevControl =
        previous.cycleControls != null
          ? // tslint:disable-next-line: no-non-null-assertion
            previous.cycleControls!.filter((a) => {
              return a.name === mp;
            })[0]
          : null;
      const currentControl =
        this.current.cycleControls != null
          ? // tslint:disable-next-line: no-non-null-assertion
            this.current.cycleControls!.filter((a) => {
              return a.name === mp;
            })[0]
          : null;

      let value = 0;
      if (prevControl != null) {
        value = prevControl.control.value;
      }

      if (currentControl != null) {
        value = value + currentControl.control.value;
      }

      return {
        name: mp,
        control: new FormControl(round(value, 2)),
      };
    });

    const totals = {
      time: new FormControl(round(previous.time.value + this.current.time.value, 2)),
      starts: new FormControl(round(previous.starts.value + this.current.starts.value, 2)),
      cycleControls,
    } as EngineDetailControls;

    return totals;
  }

  get cycleControls(): { name: string; control: AbstractControl }[] {
    const cycles = this.engine.get('cycles') as FormGroup;
    const controls =
      this.maintenanceParameters != null
        ? this.maintenanceParameters
            .map((mp) => {
              let control = cycles.get(mp === 'cycles' ? 'b1' : mp);

              // If b1 is not found, check for cycles
              if (control == null && mp === 'cycles') {
                control = cycles.get(mp);
              }

              if (control == null) {
                return undefined;
              }
              return {
                name: mp,
                // tslint:disable-next-line: no-non-null-assertion
                control: control!,
              };
            })
            .filter((c) => c != null)
            // tslint:disable-next-line: no-non-null-assertion
            .map((c) => c!)
        : [];
    return controls;
  }

  public trackCyclesControlsBy(index: any, item: any) {
    // Since we are rendering the cylce controls dynamically,
    // we need to do more manual change tracking so the input isn't
    // re-rendered when the value is changed, so the input will maintain focus
    // enabling smooth "tabbing" between inputs on the form when the value changes
    // https://stackoverflow.com/questions/42322968/angular2-dynamic-input-field-lose-focus-when-input-changes
    return index;
  }
  public ngOnInit() {}
}
